<template>
  <div class="p-6">
    <div class="flex items-center justify-between mb-4">
      <div>
        <h2 class="text-sm font-semibold text-gray-900">Call Types</h2>
        <p class="text-xs text-gray-500 mt-0.5">Manage the types of calls care managers can log.</p>
      </div>
      <div class="flex items-center gap-3">
        <label class="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
          <input v-model="showArchived" type="checkbox" class="rounded border-gray-300 text-primary-600" />
          Show archived
        </label>
        <button class="btn-primary-sm" @click="showAddForm = true">
          <FontAwesomeIcon :icon="['fas', 'plus']" class="mr-1" />Add type
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-8 text-gray-500">
      <FontAwesomeIcon :icon="['fas', 'spinner']" spin class="mr-1" />Loading...
    </div>

    <div v-else>
      <!-- Add form -->
      <div v-if="showAddForm" class="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
        <input
          ref="addInput"
          v-model="newLabel"
          type="text"
          placeholder="Call type name"
          class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
          @keydown.enter="createType"
          @keydown.escape="cancelAdd"
        />
        <div class="flex gap-2">
          <button class="btn-primary-sm text-xs" :disabled="!newLabel.trim() || creating" @click="createType">
            {{ creating ? 'Creating...' : 'Create' }}
          </button>
          <button class="btn-secondary-sm text-xs" @click="cancelAdd">Cancel</button>
        </div>
        <p v-if="createError" class="text-xs text-red-600">{{ createError }}</p>
      </div>

      <!-- Table -->
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
            <th class="py-2 px-3 font-semibold">Label</th>
            <th class="py-2 px-3 font-semibold">Key</th>
            <th class="py-2 px-3 font-semibold text-right">Calls</th>
            <th class="py-2 px-3 font-semibold text-right">Status</th>
            <th class="py-2 px-3 font-semibold text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="type in types"
            :key="type.id"
            class="border-b border-gray-100 last:border-0"
            :class="type.archived_at ? 'opacity-50' : ''"
          >
            <td class="py-2.5 px-3">
              <template v-if="editingId === type.id">
                <input
                  ref="editInput"
                  v-model="editLabel"
                  type="text"
                  class="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                  @keydown.enter="saveEdit(type)"
                  @keydown.escape="cancelEdit"
                />
              </template>
              <template v-else>
                <span class="font-medium text-gray-900">{{ type.label }}</span>
                <span v-if="type.is_system" v-tooltip="'This call type is managed by Kairon. If you need help modifying it, reach out to support@kaironhealth.com.'" class="ml-1.5 inline-flex items-center gap-1 px-1.5 py-px text-[10px] font-medium rounded bg-gray-100 text-gray-500">
                  <FontAwesomeIcon :icon="['fas', 'lock']" /> System
                </span>
              </template>
            </td>
            <td class="py-2.5 px-3 font-mono text-xs text-gray-500">{{ type.key }}</td>
            <td class="py-2.5 px-3 text-right text-gray-600 tabular-nums">{{ type.in_use_count }}</td>
            <td class="py-2.5 px-3 text-right">
              <span v-if="type.archived_at" class="text-xs text-gray-400">Archived</span>
              <span v-else class="text-xs text-green-600">Active</span>
            </td>
            <td class="py-2.5 px-3 text-right">
              <template v-if="!type.is_system">
                <template v-if="editingId === type.id">
                  <button class="text-xs text-primary-600 hover:text-primary-800 mr-2" @click="saveEdit(type)">Save</button>
                  <button class="text-xs text-gray-500 hover:text-gray-700" @click="cancelEdit">Cancel</button>
                </template>
                <template v-else>
                  <button v-if="!type.archived_at" class="text-xs text-gray-500 hover:text-gray-700 mr-2" @click="startEdit(type)">Rename</button>
                  <button v-if="type.archived_at" class="text-xs text-primary-600 hover:text-primary-800" @click="restoreType(type)">Restore</button>
                  <button v-else class="text-xs text-red-500 hover:text-red-700" @click="confirmArchive(type)">Archive</button>
                </template>
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <p v-if="!types.length" class="text-center py-8 text-sm text-gray-500">
        No call types configured. Add one to get started.
      </p>
    </div>

    <!-- Archive confirm dialog -->
    <teleport to="body">
      <div v-if="archiveTarget" class="fixed inset-0 z-[9999] flex items-center justify-center" @click.self="archiveTarget = null">
        <div class="fixed inset-0 bg-black/40" />
        <div class="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5">
          <h3 class="text-sm font-semibold text-gray-900 mb-2">Archive "{{ archiveTarget.label }}"?</h3>
          <p class="text-xs text-gray-600 mb-4">
            <span v-if="archiveTarget.in_use_count" class="font-medium text-amber-700">{{ archiveTarget.in_use_count }} calls</span>
            <span v-else>No calls</span>
            are logged as "{{ archiveTarget.label }}". Archiving hides it from new calls; existing calls keep it.
          </p>
          <div class="flex gap-2">
            <button class="btn-danger-sm grow text-xs" :disabled="archiving" @click="doArchive">
              {{ archiving ? 'Archiving...' : 'Archive' }}
            </button>
            <button class="btn-secondary-sm grow text-xs" @click="archiveTarget = null">Cancel</button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import tooltip from '@/directives/tooltip'
import {
  listPhoneCallTypes,
  createPhoneCallType,
  updatePhoneCallType,
  archivePhoneCallType,
  restorePhoneCallType,
} from '@/services/api/phoneCallTypes'

export default {
  directives: { tooltip },
  name: 'TelephonyCallTypes_Admin',
  data() {
    return {
      types: [],
      loading: true,
      showArchived: false,
      showAddForm: false,
      newLabel: '',
      creating: false,
      createError: null,
      editingId: null,
      editLabel: '',
      archiveTarget: null,
      archiving: false,
    }
  },
  watch: {
    showArchived() {
      this.load()
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        this.types = await listPhoneCallTypes({ includeArchived: this.showArchived })
      } catch {
        this.types = []
      } finally {
        this.loading = false
      }
    },
    async createType() {
      if (!this.newLabel.trim()) return
      this.creating = true
      this.createError = null
      try {
        await createPhoneCallType({ label: this.newLabel.trim() })
        this.newLabel = ''
        this.showAddForm = false
        await this.load()
      } catch (e) {
        this.createError = e?.response?.data?.error || 'Could not create call type.'
      } finally {
        this.creating = false
      }
    },
    cancelAdd() {
      this.showAddForm = false
      this.newLabel = ''
      this.createError = null
    },
    startEdit(type) {
      this.editingId = type.id
      this.editLabel = type.label
      this.$nextTick(() => this.$refs.editInput?.[0]?.focus())
    },
    cancelEdit() {
      this.editingId = null
      this.editLabel = ''
    },
    async saveEdit(type) {
      if (!this.editLabel.trim() || this.editLabel.trim() === type.label) {
        this.cancelEdit()
        return
      }
      try {
        await updatePhoneCallType(type.id, { label: this.editLabel.trim() })
        this.cancelEdit()
        await this.load()
      } catch {
        // keep editing
      }
    },
    confirmArchive(type) {
      this.archiveTarget = type
    },
    async doArchive() {
      this.archiving = true
      try {
        await archivePhoneCallType(this.archiveTarget.id)
        this.archiveTarget = null
        await this.load()
      } catch {
        // keep dialog
      } finally {
        this.archiving = false
      }
    },
    async restoreType(type) {
      try {
        await restorePhoneCallType(type.id)
        await this.load()
      } catch {
        // silent
      }
    },
  },
}
</script>
